import { isDailyQuotaLeftLessOrEqualThan } from '../validators/timeQuotaValidator';
import {
  getAccountStore,
  getDeviceAndUserStore,
  getTimeNotificationsStore,
} from '../storage/stores';
import { display } from './index';
import { isProtectionEnabled } from '../protectionStatus';
import { isDelegationStarted } from '../delegation/delegation';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { t } from '../helper/locale';

const TIME_ALMOST_UP_5_MIN_NOTIFICATION_ID =
  'TIME_ALMOST_UP_5_MIN_NOTIFICATION';
const TIME_ALMOST_UP_15_MIN_NOTIFICATION_ID =
  'TIME_ALMOST_UP_15_MIN_NOTIFICATION';

const ONE_MINUTE_IN_SECONDS = 60;
const FIVE_MINUTES_IN_SECONDS = 5 * ONE_MINUTE_IN_SECONDS;
const FIFTEEN_MINUTES_IN_SECONDS = 15 * ONE_MINUTE_IN_SECONDS;

/**
 * Returns if we should show the time quota notifications.
 *
 * We should notify when the device:
 * - Is protected
 * - Is delegated with delegation started (in the Family product the KASA already takes care of the notifications)
 * - Is not delegated by a safe network
 */
export const shouldShowTimeQuotaNotifications = () =>
  isProtectionEnabled() && isDelegationStarted() && !isSafeNetworkDelegated();

/**
 * Checks if it should display a time's up notification and shows it if needed.
 *
 * The notification should show when:
 * - The daily quota left is equal or less than 5/15 minutes.
 * - The notification didn't already show.
 */
export const notifyForDailyTimeQuotaReachedIfNeeded = async () => {
  const account = getAccountStore().get();
  const deviceUser = getDeviceAndUserStore().get();
  if (!account || !deviceUser) {
    return;
  }

  const isDailyQuotaLeftLessOrEqualThan5Minutes =
    isDailyQuotaLeftLessOrEqualThan(
      account,
      deviceUser,
      FIVE_MINUTES_IN_SECONDS
    );
  const isDailyQuotaLeftLessOrEqualThan15Minutes =
    isDailyQuotaLeftLessOrEqualThan(
      account,
      deviceUser,
      FIFTEEN_MINUTES_IN_SECONDS
    );

  // Check if the daily quota left is about 5 minutes or less but not more.
  const shouldDisplay5MinNotification =
    isDailyQuotaLeftLessOrEqualThan5Minutes &&
    !isDailyQuotaLeftLessOrEqualThan(
      account,
      deviceUser,
      FIVE_MINUTES_IN_SECONDS - ONE_MINUTE_IN_SECONDS
    ) &&
    !getTimeNotificationsStore().isNotified5MinutesForDailyQuota();

  // Check if the daily quota left is about 15 minutes or less but not more.
  const shouldDisplay15MinNotification =
    isDailyQuotaLeftLessOrEqualThan15Minutes &&
    !isDailyQuotaLeftLessOrEqualThan(
      account,
      deviceUser,
      FIFTEEN_MINUTES_IN_SECONDS - ONE_MINUTE_IN_SECONDS
    ) &&
    !getTimeNotificationsStore().isNotified15MinutesForDailyQuota();

  await displayNotificationIfNeeded(
    shouldDisplay5MinNotification,
    shouldDisplay15MinNotification
  );
  return resetNotificationIfNeeded(
    isDailyQuotaLeftLessOrEqualThan5Minutes,
    isDailyQuotaLeftLessOrEqualThan15Minutes
  );
};

const displayNotificationIfNeeded = async (
  shouldDisplay5MinNotification: boolean,
  shouldDisplay15MinNotification: boolean
) => {
  if (shouldDisplay5MinNotification) {
    console.log('5 minutes of screen time left today');
    display(
      TIME_ALMOST_UP_5_MIN_NOTIFICATION_ID,
      t('Time check'),
      t('{{minutesLeft}} minutes of screen time left today', {
        minutesLeft: '5',
      })
    );
    await getTimeNotificationsStore().setIsNotified5MinutesForDailyQuota(true);
  } else if (shouldDisplay15MinNotification) {
    console.log('15 minutes of screen time left today');
    display(
      TIME_ALMOST_UP_15_MIN_NOTIFICATION_ID,
      t('Time check'),
      t('{{minutesLeft}} minutes of screen time left today', {
        minutesLeft: '15',
      })
    );
    await getTimeNotificationsStore().setIsNotified15MinutesForDailyQuota(true);
  }
};

const resetNotificationIfNeeded = async (
  isDailyQuotaLeftLessOrEqualThan5Minutes: boolean,
  isDailyQuotaLeftLessOrEqualThan15Minutes: boolean
) => {
  if (
    !isDailyQuotaLeftLessOrEqualThan5Minutes &&
    getTimeNotificationsStore().isNotified5MinutesForDailyQuota()
  ) {
    await getTimeNotificationsStore().setIsNotified5MinutesForDailyQuota(false);
  }
  if (
    !isDailyQuotaLeftLessOrEqualThan15Minutes &&
    getTimeNotificationsStore().isNotified15MinutesForDailyQuota()
  ) {
    await getTimeNotificationsStore().setIsNotified15MinutesForDailyQuota(
      false
    );
  }
};
